<template>
  <div :style="themePageStyle">
    <!-- Header -->
    <div class="dev-topbar">
      <div class="dev-topbar-inner">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="dev-brand">
          <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:28px;"/>
          <span class="dev-brand-tag">Developers</span>
        </a>
        <div class="dev-nav">
          <a href="javascript:void(0)" @click="$router.push('/developers')" class="dev-nav-link">Apps</a>
          <a href="javascript:void(0)" @click="$router.push('/developers/docs')" class="dev-nav-link active">Docs</a>
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="dev-nav-link" style="opacity:0.7;">← Back to Flockr</a>
        </div>
      </div>
    </div>

    <div class="container py-4" style="max-width:1000px;">
      <div class="dev-kicker">Flockr API · v1</div>
      <h2 class="fw-bold mb-1" :style="{color:'var(--fm-h,#F5F0E8)'}">Documentation</h2>
      <p :style="{color:'var(--fm-s,#6B6356)',fontSize:14}">
        Read marketplace state, sync transactions, and subscribe to webhook events.
      </p>

      <!-- Downloadable PDFs -->
      <div class="dev-doc-section dev-downloads">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h4 class="mb-0" :style="{color:'var(--fm-h,#F5F0E8)'}">Download as PDF</h4>
          <span :style="{color:'var(--fm-s,#6B6356)',fontSize:11.5}">Same content, formatted for offline review &amp; sharing with stakeholders.</span>
        </div>
        <div class="d-flex gap-3 flex-wrap">
          <a href="/developer-docs/Flockr_Integration_User_Journey.pdf" download class="dev-download-card">
            <i class="mdi mdi-file-document-outline dev-download-icon"></i>
            <div>
              <div class="dev-download-title">Integration User Journey</div>
              <div class="dev-download-sub">End-to-end flow · 4 pages · 287 KB</div>
            </div>
            <i class="mdi mdi-download dev-download-arrow"></i>
          </a>
          <a href="/developer-docs/Flockr_Developer_Portal_API.pdf" download class="dev-download-card">
            <i class="mdi mdi-file-code-outline dev-download-icon"></i>
            <div>
              <div class="dev-download-title">Developer Portal &amp; API Specs</div>
              <div class="dev-download-sub">Full reference · 6 pages · 415 KB</div>
            </div>
            <i class="mdi mdi-download dev-download-arrow"></i>
          </a>
        </div>
      </div>

      <!-- Quickstart -->
      <div class="dev-doc-section">
        <h4 :style="{color:'var(--fm-h,#F5F0E8)'}">Quickstart</h4>
        <ol style="padding-left:18px;" :style="{color:'var(--fm-s,#A09888)',fontSize:13.5}">
          <li>Go to <a href="javascript:void(0)" @click="$router.push('/developers')" style="color:#BFA164;">Apps</a> and create an app.</li>
          <li>Generate an API key — <strong style="color:#F1B44C;">copy it immediately; it's only shown once</strong>.</li>
          <li>Make your first call to <code>integrationHealthz</code> with the key.</li>
          <li>Configure a webhook URL to receive real-time events.</li>
        </ol>
      </div>

      <!-- Base URL -->
      <div class="dev-doc-section">
        <h4 :style="{color:'var(--fm-h,#F5F0E8)'}">Base URL</h4>
        <code class="dev-url">{{ baseUrl }}</code>
      </div>

      <!-- Auth -->
      <div class="dev-doc-section">
        <h4 :style="{color:'var(--fm-h,#F5F0E8)'}">Authentication</h4>
        <p :style="{color:'var(--fm-s,#A09888)',fontSize:13.5}">
          Every request requires an API key. The preferred method is the
          <code>Authorization: Bearer flkr_…</code> header. Some gateways strip this header — if you
          see <code>401 Invalid or missing API key</code>, fall back to passing it as an
          <code>api_key</code> body or query parameter.
        </p>
        <pre class="dev-code"><span class="cm"># Preferred — header</span>
curl -X POST {{ baseUrl }} \
  -H <span class="st">"Authorization: Bearer flkr_live_xxxxxxxxxxxxxxxxxxxxxxxx"</span> \
  -d <span class="st">"function=integrationHealthz"</span>

<span class="cm"># Fallback — body parameter (use if your gateway strips Authorization headers)</span>
curl -X POST {{ baseUrl }} \
  -d <span class="st">"function=integrationHealthz"</span> \
  -d <span class="st">"api_key=flkr_live_xxxxxxxxxxxxxxxxxxxxxxxx"</span></pre>
      </div>

      <!-- Endpoints -->
      <div class="dev-doc-section">
        <h4 :style="{color:'var(--fm-h,#F5F0E8)'}">Endpoints</h4>

        <div v-for="ep in endpoints" :key="ep.fn" class="dev-endpoint">
          <div class="dev-endpoint-head">
            <div>
              <div class="dev-verb">POST</div>
              <code class="dev-endpoint-url">{{ baseUrl }}?function={{ ep.fn }}</code>
            </div>
            <span class="dev-pill dev-pill-key">{{ ep.tag }}</span>
          </div>
          <p :style="{color:'var(--fm-s,#A09888)',fontSize:13}" class="mb-2 mt-2">{{ ep.desc }}</p>

          <div v-if="ep.params && ep.params.length" class="mt-2">
            <div style="color:#A09888;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">Parameters</div>
            <table class="dev-table mb-2">
              <thead><tr><th>Name</th><th>Type</th><th>Description</th></tr></thead>
              <tbody>
                <tr v-for="p in ep.params" :key="p.name">
                  <td><code style="color:#E5C77E;font-size:12px;">{{ p.name }}</code></td>
                  <td style="color:#6B6356;font-size:11px;">{{ p.type }}</td>
                  <td style="color:#D8CDB6;font-size:12px;">{{ p.desc }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <pre class="dev-code mt-2">{{ ep.example }}</pre>
        </div>
      </div>

      <!-- ANITRAC Verifier (synchronous lookup) -->
      <div class="dev-doc-section" style="border:1px solid #2E8B57;background:rgba(80,200,120,0.04);">
        <h4 :style="{color:'#50C878'}"><i class="mdi mdi-shield-check me-1"></i>ANITRAC Verifier (for national-registry partners)</h4>
        <p :style="{color:'var(--fm-s,#A09888)',fontSize:13.5}">
          If your platform is the authoritative source for a livestock ID system (e.g.
          <strong>Kenya's ANITRAC central registry at the Directorate of Veterinary Services</strong>),
          register a <em>verifier URL</em> on your app in the portal. Every time a Flockr seller enters
          or updates an ANITRAC tag, Flockr makes a synchronous POST to your URL and expects a
          yes/no answer within 5 seconds. Only when you reply <code>valid:true</code> does Flockr
          display the green <strong>ANITRAC ✓</strong> trust chip on that animal's listings; until
          then a grey <em>ANITRAC pending</em> chip shows.
        </p>

        <h6 class="mt-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Flockr → your verifier (request)</h6>
        <pre class="dev-code">POST {{ '{your_verifier_url}' }}
Content-Type: application/json
X-Flockr-Signature: &lt;hmac-sha256(body, your_verifier_secret)&gt;
X-Flockr-Entity-Type: anitrac
User-Agent: Flockr-Verifier/1.0

{{ '{' }}
  <span class="key">"anitrac_id"</span>: <span class="st">"KE-CTL-2026-00041873"</span>,
  <span class="key">"context"</span>: {{ '{' }}
    <span class="key">"species_id"</span>: 1,
    <span class="key">"sex"</span>: <span class="st">"female"</span>,
    <span class="key">"weight_kg"</span>: 285,
    <span class="key">"estimated_age_months"</span>: 36
  {{ '}' }},
  <span class="key">"verified_at_request"</span>: <span class="st">"2026-06-22T10:41:24+00:00"</span>
{{ '}' }}</pre>

        <h6 class="mt-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Expected response from your registry</h6>
        <pre class="dev-code">HTTP/1.1 200 OK
Content-Type: application/json

<span class="cm">// When the tag matches a real animal in your registry:</span>
{{ '{' }}
  <span class="key">"valid"</span>: <span class="kw">true</span>,
  <span class="key">"registry_meta"</span>: {{ '{' }}
    <span class="key">"owner"</span>: <span class="st">"Mary Wanjiru · DI-PAST-00018472"</span>,
    <span class="key">"species"</span>: <span class="st">"cattle"</span>,
    <span class="key">"county"</span>: <span class="st">"Marsabit"</span>
  {{ '}' }}
{{ '}' }}

<span class="cm">// When the tag isn't recognised:</span>
{{ '{' }} <span class="key">"valid"</span>: <span class="kw">false</span>, <span class="key">"reason"</span>: <span class="st">"tag_not_found"</span> {{ '}' }}</pre>

        <h6 class="mt-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Operational notes</h6>
        <ul style="padding-left:18px;" :style="{color:'var(--fm-s,#A09888)',fontSize:13}">
          <li><strong>Timeout:</strong> Flockr waits 5 seconds maximum. Slow responses are treated as <code>valid:false</code> for that call — the chip stays grey until the next re-check.</li>
          <li><strong>Cache:</strong> Flockr caches the verification result on the animal for 5 minutes — repeated reads don't hammer your endpoint. The cache is invalidated automatically when the tag is updated.</li>
          <li><strong>Verify the HMAC:</strong> The <code>X-Flockr-Signature</code> header is HMAC-SHA256 of the raw request body using the secret Flockr generated for your app. If it doesn't match, return <code>401</code> — Flockr will treat that as <code>valid:false</code>.</li>
          <li><strong>Multiple registries:</strong> Flockr loops through every active verifier across every app — the chip lights up as soon as <em>any</em> registry confirms the tag. Useful when there are several national/regional ID systems in play.</li>
          <li><strong>Configure it:</strong> in your app's portal page, scroll to the "ANITRAC Verifier" section and paste your URL.</li>
        </ul>
      </div>

      <!-- Webhooks -->
      <div class="dev-doc-section">
        <h4 :style="{color:'var(--fm-h,#F5F0E8)'}">Webhook events</h4>
        <p :style="{color:'var(--fm-s,#A09888)',fontSize:13.5}">
          When you configure a webhook URL on an app, Flockr POSTs JSON payloads to it whenever
          subscribed events fire. Each request includes:
        </p>
        <ul style="padding-left:18px;" :style="{color:'var(--fm-s,#A09888)',fontSize:13}">
          <li><code>Content-Type: application/json</code></li>
          <li><code>X-Flockr-Event-Id: &lt;uuid&gt;</code> — unique per delivery; use it for idempotency.</li>
          <li><code>X-Flockr-Signature: &lt;hex&gt;</code> — HMAC-SHA256 of the raw body using your webhook secret.</li>
        </ul>

        <h6 class="mt-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Available events</h6>
        <table class="dev-table">
          <thead><tr><th>Event</th><th>Fires when</th></tr></thead>
          <tbody>
            <tr><td><code style="color:#E5C77E;">listing.created</code></td><td style="color:#D8CDB6;">A seller publishes a new listing on the marketplace.</td></tr>
            <tr><td><code style="color:#E5C77E;">sale.completed</code></td><td style="color:#D8CDB6;">Escrow auto-releases on a sale; ownership has transferred.</td></tr>
            <tr><td><code style="color:#E5C77E;">offtaker_order.placed</code></td><td style="color:#D8CDB6;">A buyer (offtaker) places an offer on a listing.</td></tr>
          </tbody>
        </table>

        <h6 class="mt-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Verifying the signature</h6>
        <pre class="dev-code"><span class="cm"># Node.js example</span>
<span class="kw">const</span> crypto = require(<span class="st">'crypto'</span>);
<span class="kw">function</span> <span class="fn">verify</span>(rawBody, signature, secret) {
  <span class="kw">const</span> expected = crypto.createHmac(<span class="st">'sha256'</span>, secret)
                       .update(rawBody)
                       .digest(<span class="st">'hex'</span>);
  <span class="kw">return</span> crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));
}</pre>

        <h6 class="mt-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Retry policy</h6>
        <p :style="{color:'var(--fm-s,#A09888)',fontSize:13}">
          Flockr expects a <code>2xx</code> response within 10 seconds. On failure, deliveries are
          retried 4 more times with exponential backoff (1 min, 5 min, 25 min, 2 h, 10 h). After 5
          total attempts the delivery is marked <code>failed</code> and surfaced in your app's
          Recent Deliveries log.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { flocker } from "@/api";
// Strip "/index.php" — Apache auto-routes the directory to it, and the cleaner URL
// looks better in docs (and in any partner-facing copy/paste).
const PRETTY_BASE = (flocker || '').replace(/\/index\.php\/?$/, '/');
export default {
  name: "DevelopersDocs",
  data() { return {
    baseUrl: PRETTY_BASE,
    darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    endpoints: [
      {
        fn: 'integrationHealthz', tag: 'system',
        desc: 'Liveness check. No auth required.',
        params: [],
        example: `curl -X POST ${PRETTY_BASE} \\
  -d "function=integrationHealthz"

# 200
{ "success": true, "data": { "ok": true, "version": "v1", "time": "2026-06-18T11:00:00+03:00" } }`
      },
      {
        fn: 'integrationListings', tag: 'marketplace',
        desc: 'Returns active marketplace listings. Cursor-paginated by id. Each row also includes anitrac_id (Kenya national livestock ID) and tag_number when the listing is tied to an animal — useful for traceability partners (ANITRAC / DVS / county vets).',
        params: [
          { name: 'api_key',  type: 'string', desc: 'Your API key (header or body)' },
          { name: 'since',    type: 'ISO 8601 datetime', desc: 'Return only listings updated at/after this time.' },
          { name: 'cursor',   type: 'string', desc: 'Pass the previous response\'s next_cursor for the next page.' },
          { name: 'limit',    type: 'int (1–200, default 50)', desc: 'Page size.' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -H "Authorization: Bearer flkr_live_xxx" \\
  -d "function=integrationListings" \\
  -d "since=2026-06-01T00:00:00Z" \\
  -d "limit=50"

# 200
{
  "success": true,
  "data": {
    "data": [
      { "id": "2bf5e9d6-...", "title": "Boran heifer", "species_id": 1, "price": 81225, "price_unit": "per_head", "currency": "KES", "status": "active", "updated_at": "2026-06-15 12:00:00", "anitrac_id": "KE-CTL-2026-00041873", "tag_number": "DI-TAG-001" }
    ],
    "next_cursor": "2bf5e9d6-...",
    "count": 1
  }
}`
      },
      {
        fn: 'integrationListTransactions', tag: 'sales',
        desc: 'Returns closed/completed marketplace orders.',
        params: [
          { name: 'api_key', type: 'string', desc: 'Your API key (header or body)' },
          { name: 'since',   type: 'ISO 8601 datetime', desc: 'Return only transactions updated at/after this time.' },
          { name: 'cursor',  type: 'string', desc: 'Pagination cursor (from previous response).' },
          { name: 'limit',   type: 'int (1–200)', desc: 'Page size.' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -H "Authorization: Bearer flkr_live_xxx" \\
  -d "function=integrationListTransactions" \\
  -d "limit=20"`
      },
      {
        fn: 'integrationListOfftakerOrders', tag: 'offers',
        desc: 'Returns offtaker offers/orders placed on listings.',
        params: [
          { name: 'api_key', type: 'string', desc: 'Your API key (header or body)' },
          { name: 'since',   type: 'ISO 8601 datetime', desc: 'Return only orders updated at/after this time.' },
          { name: 'cursor',  type: 'int',    desc: 'Pagination cursor (numeric).' },
          { name: 'limit',   type: 'int (1–200)', desc: 'Page size.' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -H "Authorization: Bearer flkr_live_xxx" \\
  -d "function=integrationListOfftakerOrders" \\
  -d "limit=50"`
      },
      {
        fn: 'integrationGetAnimal', tag: 'traceability',
        desc: 'Look up a single animal by any tag — NFC serial, ANITRAC ID, or ear tag number. Ideal for field vets or traceability partners scanning a physical tag. Returns identity, breed, sex, age, weight, last health / vaccination / weight record, and current marketplace status. Owner is returned as a UUID only — phone and address are never included.',
        params: [
          { name: 'api_key',  type: 'string', desc: 'Your API key (header or body)' },
          { name: 'tag',      type: 'string', desc: 'NFC serial, ANITRAC ID, or ear tag number. Match order: nfc_tag_serial → anitrac_id → tag_number.' },
          { name: 'animalId', type: 'uuid',   desc: 'Alternative: pass the Flockr animal UUID directly (from a previous listings call). If supplied, tag is ignored.' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -H "Authorization: Bearer flkr_live_xxx" \\
  -d "function=integrationGetAnimal" \\
  -d "tag=A3:3823:3738K3JJD"

# 200
{
  "success": true,
  "message": "Animal found",
  "data": {
    "animal": {
      "id": "afd87daf-6812-47a0-ba8a-45a333785871",
      "name": "Faraja",
      "tag_number": "KE-CTL-844",
      "nfc_tag_serial": "A3:3823:3738K3JJD",
      "anitrac_id": "KE-CTL-2026-00041873",
      "anitrac_verified_at": "2026-05-14 09:12:00",
      "sex": "female",
      "date_of_birth": "2024-03-11",
      "estimated_age_months": 27,
      "weight_kg": "312.50",
      "health_status": "healthy",
      "status": "active",
      "species_name": "Cattle", "species_id": 1,
      "breed_name": "Boran",   "breed_id": 12,
      "farm_id": "a66945ad-...",
      "farm_name": "Ereteti Farms",
      "farm_county_name": "Kajiado",
      "owner_user_id": "76436afb-...",
      "last_health": { "title": "Deworming", "record_date": "2026-06-20" },
      "last_vaccination": { "vaccine_name": "FMD", "next_due_date": "2026-12-20" },
      "last_weight": { "weight_kg": "312.50", "recorded_at": "2026-07-01 08:14:00" },
      "is_listed": 0,
      "listing": null
    }
  }
}`
      },
      {
        fn: 'integrationUpdateAnimalTag', tag: 'traceability',
        desc: 'Update one or more tag identifiers on an animal — the visible ear tag, the physical NFC serial, and/or the national ANITRAC ID. Common flow: partner scans a legacy tag, calls integrationGetAnimal to confirm the animal, then calls this endpoint to record the new tag. Uniqueness is enforced (no two live animals can share the same NFC or ANITRAC). Setting anitracId also stamps anitrac_verified_at + records your app as the verifier. The animal owner receives an in-app notification of the change; a partner audit line is written to audit_logs.',
        params: [
          { name: 'api_key',       type: 'string', desc: 'Your API key (header or body)' },
          { name: 'animalId',      type: 'uuid',   desc: 'Preferred — from a previous integrationGetAnimal / integrationListings call.' },
          { name: 'tag',           type: 'string', desc: 'Alternative if you don\'t have the UUID — any current tag (NFC serial / ANITRAC / ear tag) that identifies the animal.' },
          { name: 'tagNumber',     type: 'string', desc: 'Optional — new visible ear tag.' },
          { name: 'nfcTagSerial',  type: 'string', desc: 'Optional — new NFC chip serial (e.g. A3:3823:3738K3JJD).' },
          { name: 'anitracId',     type: 'string', desc: 'Optional — new ANITRAC national ID. When set, verification is stamped against your app.' },
          { name: 'note',          type: 'string', desc: 'Optional free-text note recorded in the audit log (e.g. reason for re-tagging).' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -H "Authorization: Bearer flkr_live_xxx" \\
  -d "function=integrationUpdateAnimalTag" \\
  -d "animalId=afd87daf-6812-47a0-ba8a-45a333785871" \\
  -d "nfcTagSerial=A3:3823:3738K3JJD" \\
  -d "anitracId=KE-CTL-2026-00041873" \\
  -d "note=Replaced worn NFC tag at Kitengela dip, 08 Jul 2026"

# 200
{
  "success": true,
  "message": "Animal tags updated",
  "data": {
    "animal": {
      "id": "afd87daf-...",
      "tag_number": "KE-CTL-844",
      "nfc_tag_serial": "A3:3823:3738K3JJD",
      "anitrac_id": "KE-CTL-2026-00041873",
      "anitrac_verified_at": "2026-07-08 15:22:00",
      "updated_at": "2026-07-08 15:22:00"
    },
    "changes": {
      "nfc_tag_serial": { "from": "A3:3823:0000BLANK", "to": "A3:3823:3738K3JJD" },
      "anitrac_id":     { "from": null,                 "to": "KE-CTL-2026-00041873" }
    }
  }
}

# 409 — tag already used
{
  "success": false,
  "message": "nfcTagSerial already used by another animal (tag KE-CTL-843)"
}`
      },
      {
        fn: 'integrationUpsertFarmer', tag: 'bring-your-own',
        desc: 'Push a farmer from your platform into Flockr. If the phone is new, Flockr creates the account, auto-generates a password, and sends the welcome credentials via SMS — and via email too if you supply one (recommended fallback in case SMS delivery is unreliable). If the phone already exists, Flockr just links it to your partnerFarmerId.',
        params: [
          { name: 'api_key',         type: 'string', desc: 'Your API key (header or body)' },
          { name: 'phone',           type: 'string (required)', desc: 'Farmer phone number — 0712… or 254712… both accepted' },
          { name: 'firstName',       type: 'string (required)', desc: 'Farmer first name' },
          { name: 'lastName',        type: 'string (required)', desc: 'Farmer last name' },
          { name: 'email',           type: 'string', desc: 'Optional email — when supplied, Flockr also sends welcome credentials by email (fallback channel)' },
          { name: 'countyId',        type: 'int',    desc: 'Optional Kenya county id' },
          { name: 'latitude',        type: 'decimal', desc: 'Optional GPS lat' },
          { name: 'longitude',       type: 'decimal', desc: 'Optional GPS lng' },
          { name: 'partnerFarmerId', type: 'string', desc: 'Your stable ID for this farmer — Flockr stores the mapping; later calls (eg upsertAnimal) can pass it instead of flockrFarmerId' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -d "function=integrationUpsertFarmer" \\
  -d "api_key=flkr_live_xxx" \\
  -d "phone=0712345678" \\
  -d "firstName=Mary" \\
  -d "lastName=Wanjiru" \\
  -d "countyId=22" \\
  -d "email=mary@example.com" \\
  -d "partnerFarmerId=DI-FARM-00018472"

# 200
{
  "success": true,
  "message": "Farmer created. Welcome SMS + email sent with login credentials.",
  "data": {
    "flockr_user_id": "e81358e9-...",
    "flockr_farm_id": "108a7375-...",
    "phone": "254712345678",
    "email": "mary@example.com",
    "created": true,
    "sms_sent": true,
    "email_sent": true
  }
}`
      },
      {
        fn: 'integrationUpsertAnimal', tag: 'bring-your-own',
        desc: "Push an animal into a farmer's Flockr inventory. Identify the farmer via flockrFarmerId OR partnerFarmerId. Idempotent on partnerAnimalId — re-calling updates the existing animal.",
        params: [
          { name: 'api_key',          type: 'string', desc: 'Your API key (header or body)' },
          { name: 'flockrFarmerId',   type: 'uuid',   desc: 'Either this or partnerFarmerId required.' },
          { name: 'partnerFarmerId',  type: 'string', desc: 'Either this or flockrFarmerId required — uses the mapping stored at upsertFarmer time.' },
          { name: 'partnerAnimalId',  type: 'string', desc: 'Your stable animal ID — pass this on every call for idempotency.' },
          { name: 'speciesId',        type: 'int (required)', desc: 'Flockr species id (cattle, goat, sheep, camel, poultry, etc.).' },
          { name: 'breedId',          type: 'int',    desc: 'Flockr breed id.' },
          { name: 'sex',              type: 'enum',   desc: 'male | female | unknown' },
          { name: 'tagNumber',        type: 'string', desc: 'Ear tag / RFID / barcode used by the partner.' },
          { name: 'anitracId',        type: 'string', desc: "Kenya's national livestock ID (ANITRAC tag). When set, marketplace listings render the green 'ANITRAC ✓' trust chip. Pass the exact alpha-numeric code issued by the Directorate of Veterinary Services." },
          { name: 'name',             type: 'string', desc: 'Optional pet name.' },
          { name: 'ageMonths',        type: 'int',    desc: 'Estimated age in months.' },
          { name: 'weightKg',         type: 'decimal', desc: 'Most recent weight.' },
          { name: 'acquisitionType',  type: 'enum',   desc: 'born_on_farm | purchased | donated | traded | other' },
          { name: 'acquisitionDate',  type: 'date',   desc: 'YYYY-MM-DD.' },
          { name: 'acquisitionPrice', type: 'decimal', desc: 'In KES.' },
        ],
        example: `curl -X POST ${PRETTY_BASE} \\
  -d "function=integrationUpsertAnimal" \\
  -d "api_key=flkr_live_xxx" \\
  -d "partnerFarmerId=DI-FARM-00018472" \\
  -d "partnerAnimalId=DI-ANM-00094521" \\
  -d "speciesId=1" \\
  -d "sex=female" \\
  -d "tagNumber=DI-TAG-001" \\
  -d "anitracId=KE-CTL-2026-00041873" \\
  -d "ageMonths=36" \\
  -d "weightKg=285"

# 200
{
  "success": true,
  "message": "Animal created in Flockr inventory",
  "data": {
    "flockr_animal_id": "ecea6baf-...",
    "flockr_farm_id":   "108a7375-...",
    "flockr_owner_id":  "e81358e9-...",
    "created": true
  }
}`
      },
    ],
  }},
  computed: {
    themePageStyle() { return this.darkMode ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;' : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;' },
  },
  mounted() {
    const t = localStorage.getItem('flockr_theme') || 'dark'; window.__flockrSetTheme && window.__flockrSetTheme(t);
  }
}
</script>

<style>
.dev-doc-section { background: var(--fm-bg1, #141414); border: 1px solid var(--fm-br, #1E1E1E); border-radius: 12px; padding: 22px; margin-top: 18px; }
.dev-doc-section h4 { font-size: 16px; font-weight: 700; margin-bottom: 10px; }
.dev-doc-section h6 { font-size: 13px; font-weight: 700; margin-bottom: 8px; }

.dev-url { display: inline-block; background: #1A1A1A; color: #E5C77E; padding: 8px 14px; border-radius: 6px; font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 13px; word-break: break-all; }

.dev-endpoint { background: #0F0F0F; border: 1px solid #2A2520; border-left: 3px solid #BFA164; border-radius: 8px; padding: 14px 16px; margin-bottom: 14px; }
.dev-endpoint-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.dev-verb { display: inline-block; background: #50C878; color: #0A0A0A; font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 4px; margin-right: 8px; vertical-align: middle; }
.dev-endpoint-url { color: #E5C77E; font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 12.5px; }

.dev-code { background: #0A0A0A; color: #F5F0E8; padding: 12px 14px; border-radius: 6px; font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 12px; line-height: 1.55; overflow-x: auto; white-space: pre-wrap; }
.dev-code .cm { color: #6B6356; }
.dev-code .st { color: #A0D0FF; }
.dev-code .kw { color: #FF9DC8; }
.dev-code .fn { color: #E5C77E; }

.dev-downloads { background: linear-gradient(120deg, #1a2d3a, #0e1820) !important; border: 1px solid #4DB6E5 !important; }
.dev-download-card { display: flex; align-items: center; gap: 12px; background: #0A0A0A; border: 1px solid #2A2520; border-radius: 8px; padding: 14px 16px; text-decoration: none; flex: 1; min-width: 240px; transition: border-color .15s, transform .15s; }
.dev-download-card:hover { border-color: #4DB6E5; transform: translateY(-1px); text-decoration: none; }
.dev-download-icon { font-size: 28px; color: #4DB6E5; flex-shrink: 0; }
.dev-download-title { color: #F5F0E8; font-weight: 700; font-size: 13.5px; }
.dev-download-sub { color: #A09888; font-size: 11.5px; margin-top: 1px; }
.dev-download-arrow { color: #4DB6E5; font-size: 20px; margin-left: auto; }
html[data-theme="light"] .dev-downloads { background: linear-gradient(120deg, #E8F4FB, #F4FAFD) !important; }
html[data-theme="light"] .dev-download-card { background: #FFFFFF; border-color: #D9E3EA; }
html[data-theme="light"] .dev-download-title { color: #1A1A1A; }
</style>
